import { NavigationFailureType, isNavigationFailure } from 'vue-router';
import { isEqual } from 'lodash';
// 与 Vue Router 的 key / scroll state 分开保存，避免覆盖平台已有历史信息。
const REACT_HISTORY_STATE_KEY = '__wujiGogoReactState';
let entrySequence = 0;
function createEntry(path, value) {
    entrySequence += 1;
    const random = Math.random().toString(36);
    return { path, value: value ?? null, key: `${Date.now().toString(36)}-${entrySequence}-${random.slice(2)}` };
}
function isSameLocation(a, b) {
    return a.path === b.path && a.hash === b.hash && isEqual(a.query, b.query);
}
/** React 远端页面的路由直接读写平台浏览器历史（runtime-remote 的 ReactRemoteHistory）。 */
export function createReactNavigationAdapter(router, target = window) {
    const listeners = new Set();
    const notify = () => listeners.forEach(listener => listener());
    let pending;
    let pendingPop;
    let navigationType = 'POP';
    function readEntry() {
        const saved = target.history.state?.[REACT_HISTORY_STATE_KEY];
        return saved?.path === router.currentRoute.fullPath && typeof saved.key === 'string' ? saved : undefined;
    }
    function writeEntry(entry, push = false) {
        target.history[push ? 'pushState' : 'replaceState']({
            ...target.history.state,
            [REACT_HISTORY_STATE_KEY]: entry,
        }, '');
    }
    // 初次打开和刷新都给当前条目稳定的 React key；不改 Vue Router 的 key / scroll 信息。
    const existing = target.history.state?.[REACT_HISTORY_STATE_KEY];
    let currentEntry = readEntry() || createEntry(router.currentRoute.fullPath, existing?.path === router.currentRoute.fullPath ? existing.value : null);
    writeEntry(currentEntry);
    function complete(navigation, duplicated = false) {
        if (pending !== navigation)
            return;
        if (duplicated && !isSameLocation(navigation.redirectRoute || navigation.target, router.currentRoute)) {
            pending = undefined;
            return;
        }
        const entry = createEntry(router.currentRoute.fullPath, navigation.state);
        // 只补 Vue Router 已确认的同 URL 导航；其他跳转仍由它执行守卫、同步和浏览器历史操作。
        // 此处保留平台 key，React 条目标识独立存储。
        writeEntry(entry, duplicated && !navigation.replace);
        currentEntry = entry;
        pending = undefined;
        pendingPop = undefined;
        navigationType = navigation.replace ? 'REPLACE' : 'PUSH';
        notify();
    }
    function syncEntry(isPop) {
        const saved = readEntry();
        if (saved?.key === currentEntry.key && !isPop)
            return;
        const previous = target.history.state?.[REACT_HISTORY_STATE_KEY];
        navigationType = previous?.key === currentEntry.key ? 'REPLACE' : 'PUSH';
        if (isPop)
            navigationType = 'POP';
        currentEntry = saved || createEntry(router.currentRoute.fullPath, null);
        if (!saved)
            writeEntry(currentEntry);
    }
    // 平台守卫追加公共 query 的重定向仍属于同一页面；权限等跨页面重定向不带入用户 state。
    function isQueryRedirect(navigation, route) {
        return route.path === navigation.target.path
            && route.hash === navigation.target.hash
            && Object.entries(navigation.target.query).every(([key, value]) => isEqual(route.query[key], value));
    }
    const removeAfterEach = router.afterEach((to) => {
        const navigation = pending;
        if (navigation?.redirected) {
            if (navigation.redirectRoute === to) {
                complete(navigation);
            }
            else if (navigation.redirectRoute || !isQueryRedirect(navigation, to)) {
                pending = undefined;
            }
            else {
                // 同步守卫可能在捕获重定向 transition 的微任务执行前就完成。
                navigation.completedRoute = to;
            }
        }
        if (pending !== navigation || !navigation?.completedRoute) {
            syncEntry(Boolean(pendingPop));
            pendingPop = undefined;
            notify();
        }
    });
    const onPopstate = () => {
        pending = undefined;
        const pop = {};
        pendingPop = pop;
        // 捕获阶段先标记 POP，让同步完成的 Vue 守卫也能读到正确动作。
        // 同 URL 的 pop 不触发 afterEach，需要在 Router 处理完事件后通知 React。
        void Promise.resolve().then(() => {
            if (pendingPop !== pop)
                return;
            const currentHref = new URL(router.resolve(router.currentRoute.fullPath).href, target.location.href).href;
            if (currentHref !== target.location.href)
                return; // 异步守卫尚未完成。
            syncEntry(true);
            pendingPop = undefined;
            notify();
        });
    };
    target.addEventListener('popstate', onPopstate, true);
    function followQueryRedirect() {
        const navigation = pending;
        if (!navigation)
            return;
        navigation.redirected = true;
        void Promise.resolve().then(() => {
            if (pending !== navigation)
                return;
            // Vue Router 3 的守卫重定向不会继承 push/replace 回调。只读 pending，
            // 将 state 绑定到这一轮实际 Route 对象，取消后相同 URL 的新导航不能领取旧 state。
            const { history } = router;
            const route = navigation.completedRoute || history.pending;
            if (!route || !isQueryRedirect(navigation, route)) {
                pending = undefined;
                return;
            }
            navigation.redirectRoute = route;
            if (router.currentRoute === route)
                complete(navigation);
            else if (isSameLocation(route, router.currentRoute)
                && route.matched.length === router.currentRoute.matched.length
                && route.matched[route.matched.length - 1] === router.currentRoute.matched[route.matched.length - 1]) {
                // 公共 query 补全也可能重定向回当前 URL，Vue Router 同样不会调用 afterEach。
                complete(navigation, true);
            }
        });
    }
    return {
        getPath: () => router.currentRoute.fullPath,
        navigate(path, replace = false, state) {
            const navigation = {
                state,
                replace,
                target: router.resolve(path).route,
                redirected: false,
            };
            pendingPop = undefined;
            pending = navigation;
            router[replace ? 'replace' : 'push'](path, () => complete(navigation), (error) => {
                if (pending !== navigation)
                    return;
                if (isNavigationFailure(error, NavigationFailureType.redirected)) {
                    followQueryRedirect();
                }
                else if (isNavigationFailure(error, NavigationFailureType.duplicated)) {
                    complete(navigation, true);
                }
                else {
                    pending = undefined;
                    if (error && !isNavigationFailure(error)) {
                        console.error('[gogo-react] navigation failed', error);
                    }
                }
            });
        },
        go(delta) {
            pending = undefined;
            router.go(delta);
        },
        resolveHref(path) {
            return router.resolve(path).href;
        },
        getState() {
            const saved = target.history.state?.[REACT_HISTORY_STATE_KEY];
            return saved?.path === router.currentRoute.fullPath ? saved.value : null;
        },
        getKey() {
            return readEntry()?.key || currentEntry.key;
        },
        getNavigationType() {
            return navigationType;
        },
        subscribe(listener) {
            listeners.add(listener);
            return () => {
                listeners.delete(listener);
            };
        },
        dispose() {
            pending = undefined;
            pendingPop = undefined;
            removeAfterEach();
            target.removeEventListener('popstate', onPopstate, true);
            listeners.clear();
        },
    };
}
